"use strict";
/*
 * 8x8 Confidential & Proprietary || Copyright 2025 8x8, Inc. or its affiliates. All Rights Reserved.
 *  _____      _____
 * |  _  |    |  _  |
 *  \ V /__  __\ V /   ___ ___  _ __ ___
 *  / _ \\ \/ // _ \  / __/ _ \| '_ ` _ \
 * | |_| |>  <| |_| || (_| (_) | | | | | |
 * \_____/_/\_\_____(_)___\___/|_| |_| |_|
 *
 * @License https://www.8x8.com/terms-and-conditions/software-development-kit-license-agreement
 * @Author Comanici Paul
 * @Date 2025/08/28
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.transformFileToChunks = transformFileToChunks;
exports.validateChunkIntegrity = validateChunkIntegrity;
const tslib_1 = require("tslib");
const dependencies_1 = require("./dependencies");
const observability_codes_1 = require("./observability.codes");
const crypto_1 = require("./crypto");
const config_1 = require("./config");
function transformFileToChunks(file_1, fileId_1) {
    return tslib_1.__awaiter(this, arguments, void 0, function* (file, fileId, options = {}, instanceId) {
        const chunkSize = options.chunkSize || config_1.CHUNK_SIZE;
        const generateHash = options.generateHash !== false;
        // Disable encryption if crypto.subtle is not available (e.g., in test environments)
        const cryptoAvailable = typeof crypto !== "undefined" && typeof crypto.subtle !== "undefined";
        const shouldEncrypt = options.encrypt !== false && cryptoAvailable;
        try {
            (0, dependencies_1.pushLog)(instanceId, [`Starting file transformation for ${file.name}`], {
                context: {
                    code: observability_codes_1.FILE_CODES.FILE_TRANSFORM_START,
                    fileName: file.name,
                    fileSize: file.size,
                    chunkSize,
                    encrypted: shouldEncrypt,
                },
            });
            // Generate encryption key if needed
            let encryptionKey = null;
            let encryptionKeyData = null;
            if (shouldEncrypt) {
                const keyPair = yield (0, crypto_1.generateEncryptionKey)(instanceId);
                encryptionKey = keyPair.key;
                encryptionKeyData = keyPair.keyData;
            }
            // Generate file metadata
            const metadata = {
                id: fileId,
                name: file.name,
                size: file.size,
                type: file.type,
                lastModified: file.lastModified,
                encrypted: shouldEncrypt,
                encryptionKey: shouldEncrypt && encryptionKeyData ? (0, crypto_1.arrayBufferToBase64)(encryptionKeyData) : undefined,
            };
            // Generate hash if requested
            if (generateHash) {
                try {
                    metadata.hash = yield generateFileHash(file);
                }
                catch (error) {
                    (0, dependencies_1.pushError)(instanceId, error, {
                        context: { code: observability_codes_1.FILE_CODES.FILE_HASH_GENERATION_ERROR, fileName: file.name },
                    });
                    // Continue without hash if generation fails
                }
            }
            // Split file into chunks
            const chunks = [];
            const totalChunks = Math.ceil(file.size / chunkSize);
            for (let i = 0; i < totalChunks; i++) {
                const start = i * chunkSize;
                const end = Math.min(start + chunkSize, file.size);
                const chunkBlob = file.slice(start, end);
                try {
                    let chunkData;
                    let iv;
                    if (shouldEncrypt && encryptionKey) {
                        // Encrypt chunk
                        const chunkArrayBuffer = yield chunkBlob.arrayBuffer();
                        const encryptionResult = yield (0, crypto_1.encryptData)(chunkArrayBuffer, encryptionKey, instanceId);
                        chunkData = encryptionResult.encryptedData;
                        iv = encryptionResult.iv;
                    }
                    else {
                        // Use ArrayBuffer directly without encryption
                        chunkData = yield chunkBlob.arrayBuffer();
                    }
                    chunks.push({
                        chunkId: `${fileId}_chunk_${i}`,
                        fileId,
                        chunkIndex: i,
                        totalChunks,
                        data: chunkData,
                        iv: iv,
                        size: chunkBlob.size,
                    });
                }
                catch (error) {
                    throw new Error(`${dependencies_1.ERROR_PREFIX} Failed to process chunk ${i}: ${error}`);
                }
            }
            (0, dependencies_1.pushLog)(instanceId, [`File transformation completed for ${file.name}`], {
                context: {
                    code: observability_codes_1.FILE_CODES.FILE_TRANSFORM_COMPLETE,
                    fileName: file.name,
                    totalChunks,
                    hasHash: !!metadata.hash,
                },
            });
            return { metadata, chunks };
        }
        catch (error) {
            (0, dependencies_1.pushError)(instanceId, error, {
                context: { code: observability_codes_1.FILE_CODES.FILE_TRANSFORM_ERROR, fileName: file.name },
            });
            throw error;
        }
    });
}
function validateChunkIntegrity(chunks, expectedSize, instanceId) {
    var _a;
    try {
        // Validate chunk sequence
        const sortedChunks = [...chunks].sort((a, b) => a.chunkIndex - b.chunkIndex);
        for (let i = 0; i < sortedChunks.length; i++) {
            if (sortedChunks[i].chunkIndex !== i) {
                (0, dependencies_1.pushError)(instanceId, new Error(`${dependencies_1.ERROR_PREFIX} Missing chunk at index ${i}`), {
                    context: { code: observability_codes_1.FILE_CODES.CHUNK_SEQUENCE_ERROR },
                });
                return false;
            }
        }
        // Validate total size
        const totalChunkSize = chunks.reduce((sum, chunk) => sum + chunk.size, 0);
        if (totalChunkSize !== expectedSize) {
            (0, dependencies_1.pushError)(instanceId, new Error(`${dependencies_1.ERROR_PREFIX} Chunk size mismatch: expected ${expectedSize}, got ${totalChunkSize}`), {
                context: { code: observability_codes_1.FILE_CODES.CHUNK_SIZE_MISMATCH },
            });
            return false;
        }
        // Validate all chunks belong to the same file
        const fileId = (_a = chunks[0]) === null || _a === void 0 ? void 0 : _a.fileId;
        const allChunksBelongToSameFile = chunks.every((chunk) => chunk.fileId === fileId);
        if (!fileId || !allChunksBelongToSameFile) {
            (0, dependencies_1.pushError)(instanceId, new Error(`${dependencies_1.ERROR_PREFIX} Chunks belong to different files`), {
                context: { code: observability_codes_1.FILE_CODES.CHUNK_FILE_ID_MISMATCH },
            });
            return false;
        }
        return true;
    }
    catch (error) {
        (0, dependencies_1.pushError)(instanceId, error, {
            context: { code: observability_codes_1.FILE_CODES.CHUNK_VALIDATION_ERROR },
        });
        return false;
    }
}
function generateFileHash(file) {
    return tslib_1.__awaiter(this, void 0, void 0, function* () {
        const buffer = yield file.arrayBuffer();
        const hashBuffer = yield crypto.subtle.digest("SHA-256", buffer);
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
    });
}
//# sourceMappingURL=transformer.js.map